import { curSym } from "@/lib/company";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PriceInput } from "@/components/PriceInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Plus, Search, X, Layers, Tag, Boxes, TrendingUp, TrendingDown, Play, RotateCcw, Loader2, CheckCircle2, Timer, Percent } from "lucide-react";
import { fmtTL } from "@/components/StatusBadge";

const RATES = [3, 5, 8, 10];
const STATUS_META = {
  scheduled: { label: "Bekliyor", cls: "bg-amber-100 text-amber-700" },
  active: { label: "Aktif", cls: "bg-emerald-100 text-emerald-700" },
  ended: { label: "Bitti", cls: "bg-slate-100 text-slate-500" },
  cancelled: { label: "İptal", cls: "bg-red-100 text-red-600" },
};

// datetime-local (yerel) -> ISO (UTC)
const toIso = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d.toISOString(); };
const fmtWhen = (iso) => { if (!iso) return "—"; const d = new Date(iso); return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); };
const countdown = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "şimdi";
  const h = Math.floor(ms / 3600000), m = Math.round((ms % 3600000) / 60000);
  return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
};

export default function ScheduledPricing() {
  const { can, activeSector } = useAuth();
  const canManage = can("price.write");

  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const blank = { name: "", mode: "pct", fixed_price: "", direction: "zam", pct: 5, scope: "category", categories: [], product_ids: [], products: [], timing: "now", starts_at: "", ends_at: "", note: "" };
  const [form, setForm] = useState(blank);
  const [pq, setPq] = useState("");
  const [results, setResults] = useState([]);

  const load = useCallback(() => {
    api.get("/price-rules?status=upcoming").then((r) => setActive(r.data)).catch(() => {});
    api.get("/price-rules?status=history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, [load]);

  const searchProducts = useCallback(() => {
    if (!pq) { setResults([]); return; }
    api.get(`/products?q=${encodeURIComponent(pq)}&page_size=8`).then((r) => setResults(r.data.items || [])).catch(() => {});
  }, [pq]);
  useEffect(() => { const t = setTimeout(searchProducts, 300); return () => clearTimeout(t); }, [searchProducts]);

  const reset = () => { setForm(blank); setPq(""); setResults([]); };
  const toggleCat = (name) => setForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name] }));
  const addProduct = (p) => setForm((f) => f.product_ids.includes(p.id) ? f : ({ ...f, product_ids: [...f.product_ids, p.id], products: [...f.products, p] }));
  const removeProduct = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.filter((x) => x !== id), products: f.products.filter((p) => p.id !== id) }));

  const submit = async () => {
    const isFixed = form.mode === "fixed";
    if (isFixed) {
      if (!(Number(form.fixed_price) > 0)) return toast.error("Sabit fiyat 0'dan büyük olmalı");
      if (form.product_ids.length === 0) return toast.error("Sabit fiyat için en az bir ürün seçin");
    } else {
      if (!(Number(form.pct) > 0)) return toast.error("Oran %0'dan büyük olmalı");
      if (form.scope === "category" && form.categories.length === 0) return toast.error("En az bir kategori seçin");
      if (form.scope === "products" && form.product_ids.length === 0) return toast.error("En az bir ürün seçin");
    }
    if ((form.note || "").trim().length < 10) return toast.error("Açıklama zorunludur ve en az 10 karakter olmalıdır.");
    if (form.timing === "scheduled" && !form.starts_at) return toast.error("Başlangıç zamanı seçin");
    if (!isFixed && form.scope === "all" && !window.confirm("Bu kural TÜM ürünlerin perakende fiyatını etkileyecek. Devam edilsin mi?")) return;
    setBusy("save");
    try {
      const { data } = await api.post("/price-rules", {
        name: form.name.trim() || undefined,
        mode: form.mode,
        fixed_price: isFixed ? Number(form.fixed_price) : undefined,
        direction: form.direction,
        pct: isFixed ? 0 : Number(form.pct),
        scope: isFixed ? "products" : form.scope,
        categories: (!isFixed && form.scope === "category") ? form.categories : [],
        product_ids: (isFixed || form.scope === "products") ? form.product_ids : [],
        activate_now: form.timing === "now",
        starts_at: form.timing === "scheduled" ? toIso(form.starts_at) : null,
        ends_at: toIso(form.ends_at),
        note: form.note.trim(),
        sectors: activeSector && activeSector !== "full" ? [activeSector] : undefined,
      });
      if (data?.pending_approval) toast.success(data.message || "Fiyat kuralı üstlerinizin onayına gönderildi — çoğunluk onaylayınca aktifleşir.");
      else toast.success(form.timing === "now" ? "Fiyat kuralı uygulandı" : "Fiyat kuralı zamanlandı");
      setOpen(false); reset(); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(null);
  };

  const act = async (rule, action, label) => {
    setBusy(rule.id + action);
    try {
      if (action === "delete") await api.delete(`/price-rules/${rule.id}`);
      else await api.post(`/price-rules/${rule.id}/${action}`, {});
      toast.success(label); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(null);
  };

  const scopeText = (r) => r.scope === "all" ? "Tüm ürünler" : r.scope === "category" ? `${(r.categories || []).length} kategori` : `${(r.product_ids || []).length} ürün`;

  const RuleCard = ({ r }) => {
    const meta = STATUS_META[r.status] || STATUS_META.ended;
    const isZam = r.direction === "zam";
    return (
      <div data-testid={`price-rule-${r.id}`} className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-800">{r.name}</p>
            <p className="mt-0.5 text-xs text-slate-500">{scopeText(r)}{r.note ? ` · ${r.note}` : ""}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          {r.mode === "fixed" ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-brand-green/10 px-2.5 py-1 text-lg font-bold tabular text-brand-green">
              <Tag className="h-4 w-4" /> {fmtTL(r.fixed_price)}
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-lg font-bold tabular ${isZam ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
              {isZam ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} %{r.pct}
            </span>
          )}
          <span className="text-xs text-slate-400">{r.mode === "fixed" ? "sabit fiyat" : (isZam ? "zam" : "indirim")}{r.affected_count ? ` · ${r.affected_count} ürün` : ""}</span>
        </div>
        <div className="mt-2 space-y-0.5 text-[11px] text-slate-400">
          {r.status === "scheduled" && r.starts_at && <p className="flex items-center gap-1"><Timer className="h-3 w-3" /> Başlar: {fmtWhen(r.starts_at)} {countdown(r.starts_at) && <span className="text-amber-600">({countdown(r.starts_at)} sonra)</span>}</p>}
          {r.status === "active" && r.ends_at && <p className="flex items-center gap-1"><Timer className="h-3 w-3" /> Otomatik biter: {fmtWhen(r.ends_at)} {countdown(r.ends_at) && <span className="text-emerald-600">({countdown(r.ends_at)} kaldı)</span>}</p>}
          {r.status === "active" && !r.ends_at && <p className="italic">Manuel sonlandırılana kadar aktif</p>}
        </div>
        {canManage && (r.status === "scheduled" || r.status === "active") && (
          <div className="mt-3 flex flex-wrap gap-2">
            {r.status === "scheduled" && (
              <Button data-testid={`rule-activate-${r.id}`} onClick={() => act(r, "activate", "Kural başlatıldı")} disabled={!!busy} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700">
                {busy === r.id + "activate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="mr-1 h-4 w-4" /> Şimdi Başlat</>}
              </Button>
            )}
            {r.status === "active" && (
              <Button data-testid={`rule-stop-${r.id}`} onClick={() => act(r, "stop", "Kural sonlandı, fiyatlar eski haline döndü")} disabled={!!busy} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                {busy === r.id + "stop" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="mr-1 h-4 w-4" /> Sonlandır (eski fiyat)</>}
              </Button>
            )}
            <Button data-testid={`rule-delete-${r.id}`} onClick={() => act(r, "delete", "Kural iptal edildi")} disabled={!!busy} size="sm" variant="outline" className="border-slate-300 text-slate-600">
              <X className="mr-1 h-4 w-4" /> İptal
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5 fade-in" data-testid="scheduled-pricing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Clock className="h-4 w-4 text-brand-green" /> Zamanlı Zam / İndirim Kuralları</div>
        {canManage && <Button data-testid="new-price-rule-btn" onClick={() => { reset(); setOpen(true); }} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Kural</Button>}
      </div>
      <p className="text-sm text-slate-500">
        Seçili ürün/kategori/tüm ürünlere <b>%zam veya %indirim</b> uygulayın — <b>hemen</b> ya da belirlediğiniz <b>saatte</b> başlasın, isterseniz belirli bir saatte <b>otomatik</b> eski fiyata dönsün.
        Örn. <i>"22:00'de alkole %3 zam, 2 saat sonra kapansın"</i>. Dilediğiniz an <b>Sonlandır</b> ile anında eski fiyata dönebilirsiniz. Yalnızca yetkili kullanıcılar açıp kapatabilir.
      </p>

      {active.length === 0 ? (
        <div className="rounded-md bg-white p-10 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">Aktif veya bekleyen kural yok.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="price-rules-active">
          {active.map((r) => <RuleCard key={r.id} r={r} />)}
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-3 pt-2">
          <h2 className="border-t border-slate-200 pt-4 font-heading text-lg font-semibold text-slate-800">Geçmiş</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="price-rules-history">
            {history.map((r) => <RuleCard key={r.id} r={r} />)}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="price-rule-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Yeni Zamanlı Fiyat Kuralı</DialogTitle>
            <DialogDescription>Perakende (POS) fiyatına geçici %zam/indirim. Sonlandırıldığında veya süresi dolduğunda fiyatlar otomatik eski haline döner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fiyat Tipi</Label>
              <div className="mt-1 flex gap-2">
                <button type="button" data-testid="rule-mode-pct" onClick={() => setForm({ ...form, mode: "pct" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.mode === "pct" ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Percent className="h-4 w-4" /> % Oran</button>
                <button type="button" data-testid="rule-mode-fixed" onClick={() => setForm({ ...form, mode: "fixed", scope: "products" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.mode === "fixed" ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Tag className="h-4 w-4" /> Sabit Fiyat ({curSym()})</button>
              </div>
              {form.mode === "fixed" && <p className="mt-1 text-[11px] text-slate-400">Seçili ürünlerin perakende fiyatı belirttiğiniz sabit {curSym()} değere ayarlanır; kural sonlandığında/süresi dolduğunda otomatik eski fiyata döner.</p>}
            </div>
            {form.mode === "pct" ? (<>
            <div>
              <Label>Yön</Label>
              <div className="mt-1 flex gap-2">
                <button type="button" data-testid="rule-dir-zam" onClick={() => setForm({ ...form, direction: "zam" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.direction === "zam" ? "bg-red-600 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><TrendingUp className="h-4 w-4" /> Zam</button>
                <button type="button" data-testid="rule-dir-indirim" onClick={() => setForm({ ...form, direction: "indirim" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.direction === "indirim" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><TrendingDown className="h-4 w-4" /> İndirim</button>
              </div>
            </div>
            <div>
              <Label>Oran (%)</Label>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex gap-1.5">
                  {RATES.map((r) => (
                    <button key={r} type="button" data-testid={`rule-rate-${r}`} onClick={() => setForm({ ...form, pct: r })}
                      className={`rounded-md px-3 py-2 text-sm font-bold ${Number(form.pct) === r ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>%{r}</button>
                  ))}
                </div>
                <Input data-testid="rule-pct-input" type="number" min="0" max="90" step="0.5" value={form.pct} onChange={(e) => setForm({ ...form, pct: e.target.value })} className="h-10 w-24 tabular" placeholder="Özel %" />
              </div>
            </div>
            </>) : (
            <div>
              <Label>Sabit Fiyat ({curSym()})</Label>
              <PriceInput data-testid="rule-fixed-price" type="number" min="0" step="0.01" value={form.fixed_price} onChange={(e) => setForm({ ...form, fixed_price: e.target.value })} wrapperClassName="mt-1 w-40" className="h-10 tabular" placeholder="0,00" />
            </div>
            )}
            {form.mode === "pct" && (<div>
              <Label>Kapsam</Label>
              <div className="mt-1 flex gap-2">
                <button type="button" data-testid="rule-scope-all" onClick={() => setForm({ ...form, scope: "all" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.scope === "all" ? "bg-slate-800 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Boxes className="h-4 w-4" /> Tümü</button>
                <button type="button" data-testid="rule-scope-category" onClick={() => setForm({ ...form, scope: "category" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.scope === "category" ? "bg-slate-800 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Layers className="h-4 w-4" /> Kategori</button>
                <button type="button" data-testid="rule-scope-products" onClick={() => setForm({ ...form, scope: "products" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.scope === "products" ? "bg-slate-800 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Tag className="h-4 w-4" /> Ürünler</button>
              </div>
            </div>)}
            {form.mode === "fixed" && <p className="-mt-1 text-[11px] font-medium text-slate-500">Kapsam: <b>Ürünler</b> (sabit fiyatta zorunlu). Aşağıdan ürünleri seçin.</p>}
            {form.scope === "category" && (
              <div>
                <Label>Kategoriler <span className="text-xs font-normal text-slate-400">({form.categories.length} seçili)</span></Label>
                <div className="mt-1 max-h-40 space-y-1 overflow-auto rounded-md border border-slate-200 p-2">
                  {categories.length === 0 && <p className="text-xs text-slate-400">Kategori yok.</p>}
                  {categories.map((c) => (
                    <label key={c.id} data-testid={`rule-category-${c.id}`} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                      <input type="checkbox" checked={form.categories.includes(c.name)} onChange={() => toggleCat(c.name)} className="accent-brand-green" />
                      <span className="text-slate-700">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.scope === "products" && (
              <div>
                <Label>Ürünler <span className="text-xs font-normal text-slate-400">({form.product_ids.length} seçili)</span></Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="rule-product-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Ürün ara ve ekle..." className="pl-9" />
                </div>
                {results.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-auto rounded-md border border-slate-200">
                    {results.map((p) => (
                      <button key={p.id} type="button" data-testid={`rule-product-add-${p.id}`} onClick={() => { addProduct(p); setPq(""); setResults([]); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50">
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.products.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {form.products.map((p) => (
                      <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-600">
                        {p.name}<button type="button" onClick={() => removeProduct(p.id)} className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>Zamanlama</Label>
              <div className="mt-1 flex gap-2">
                <button type="button" data-testid="rule-timing-now" onClick={() => setForm({ ...form, timing: "now" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.timing === "now" ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Play className="h-4 w-4" /> Hemen başlat</button>
                <button type="button" data-testid="rule-timing-scheduled" onClick={() => setForm({ ...form, timing: "scheduled" })}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${form.timing === "scheduled" ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}><Timer className="h-4 w-4" /> Zamanla</button>
              </div>
            </div>
            {form.timing === "scheduled" && (
              <div>
                <Label>Başlangıç zamanı</Label>
                <Input data-testid="rule-starts-at" type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1" />
              </div>
            )}
            <div>
              <Label>Otomatik bitiş <span className="text-xs font-normal text-slate-400">(opsiyonel — boşsa manuel sonlandırırsınız)</span></Label>
              <Input data-testid="rule-ends-at" type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Ad <span className="text-xs font-normal text-slate-400">(opsiyonel)</span></Label>
              <Input data-testid="rule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn. Gece alkol zammı" className="mt-1" />
            </div>
            <div>
              <Label>Açıklama * <span className="text-xs font-normal text-slate-400">(en az 10 karakter)</span></Label>
              <Input data-testid="rule-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Örn. Gece tarifesi — alkollü ürünlerde geçici zam" className="mt-1" />
              <p className={`mt-0.5 text-[11px] ${(form.note || "").trim().length < 10 ? "text-red-500" : "text-emerald-600"}`}>{(form.note || "").trim().length}/10 karakter</p>
            </div>
            <Button data-testid="rule-submit" onClick={submit} disabled={busy === "save"} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} {form.timing === "now" ? "Kuralı Uygula" : "Kuralı Zamanla"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
